import { alpha } from '@mui/material/styles';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { gray } from '../../getDesignTokens';

/**
 * MuiIconButton — the icon-only button used across list rows, dialogs,
 * and toolbars. Renders as a bordered chip by default so it visually
 * matches outlined text buttons in the same row.
 *
 * Special case: icon buttons INSIDE inputs (DatePicker's calendar icon,
 * InputAdornment icons) and inside DataGrid cells and headers strip the
 * border + background so they read as part of that control, not as a
 * separate chip. See STRIPPED_CONTEXTS.
 *
 * The `plain` variant is a custom escape hatch — a button with no
 * chrome at all (e.g., a close X in a dense header). Rarely used;
 * prefer the default when possible.
 */
const STRIPPED_CONTEXTS = [
  '.MuiInputBase-root &',
  '.MuiInputAdornment-root &',
  '.MuiDataGrid-cell &',
  '.MuiDataGrid-columnHeader &',
].join(', ');

export const iconButtonCustomizations = {
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: 'none',
        borderRadius: theme.shape.borderRadius,
        textTransform: 'none',
        fontWeight: theme.typography.fontWeightMedium,
        letterSpacing: 0,
        color: theme.palette.text.primary,
        border: '1px solid ',
        borderColor: gray[200],
        backgroundColor: alpha(gray[50], 0.3),
        '&:hover': {
          backgroundColor: gray[100],
          borderColor: gray[300],
        },
        '&:active': {
          backgroundColor: gray[200],
        },
        // No border where the button sits inside another control — an input
        // (DatePicker's calendar icon) or a grid row / header (row actions,
        // the sort arrow). A boxed icon on every row read as a column of chips.
        [STRIPPED_CONTEXTS]: {
          border: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: alpha(gray[200], 0.5),
            borderColor: 'transparent',
          },
          '&:active': {
            backgroundColor: alpha(gray[200], 0.7),
          },
        },
        ...theme.applyStyles('dark', {
          backgroundColor: gray[800],
          borderColor: gray[700],
          '&:hover': {
            backgroundColor: gray[900],
            borderColor: gray[600],
          },
          '&:active': {
            backgroundColor: gray[900],
          },
          [STRIPPED_CONTEXTS]: {
            border: 'none',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: alpha(gray[700], 0.5),
              borderColor: 'transparent',
            },
          },
        }),
      }),
    },
    variants: [
      {
        props: {
          variant: 'plain',
        },
        style: {
          border: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:hover': { backgroundColor: 'transparent' },
          '&:active': { backgroundColor: 'transparent' },
        },
      },
      {
        props: {
          size: 'small',
        },
        style: {
          width: '2.25rem',
          height: '2.25rem',
          padding: '0.25rem',
          [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
        },
      },
      {
        props: {
          size: 'medium',
        },
        style: {
          width: '2.5rem',
          height: '2.5rem',
        },
      },
    ],
  },
};
